import { Controller } from "@hotwired/stimulus"

// Narrows rows already on the page: each `control` target is a criterion named
// by its `name`, matched against the rows' data-filter-<name>.
// Used by enrollments/index (student) and school_texts/show (text, category, customised).
// A `clear` target is shared/_filter_search's ✕.
// Contract, reserved names, traps: documentation/development_manual/client-side-filter.md
const TEXT_TYPES = ["text", "search"]

export default class extends Controller {
  static targets = ["control", "row", "section", "empty", "clear"]

  // Re-apply after a form restoration and after a streamed replace of a section
  // or a row; all three also fire on the initial connect, where the guard keeps them free.
  connect() {
    if (this.criteria.length > 0) this.filter()
  }

  sectionTargetConnected() {
    if (this.criteria.length > 0) this.filter()
  }

  rowTargetConnected() {
    if (this.criteria.length > 0) this.filter()
  }

  filter() {
    const criteria = this.criteria
    const active = criteria.length > 0

    this.rowTargets.forEach((row) => {
      row.hidden = active && !criteria.every((criterion) => this.matches(row, criterion))
    })
    this.sectionTargets.forEach((section) => {
      section.hidden = active && !section.querySelector("[data-filter-target~='row']:not([hidden])")
    })
    if (this.hasEmptyTarget) {
      this.emptyTarget.hidden = !active || this.rowTargets.some((row) => !row.hidden)
    }
    this.clearTargets.forEach((button) => {
      button.hidden = !this.controlTargets.some((control) => control.name === button.dataset.filterNameParam && control.value !== "")
    })
  }

  clear({ params: { name } }) {
    const controls = this.controlTargets.filter((control) => control.name === name)
    controls.forEach((control) => {
      control.value = ""
    })
    controls[0]?.focus()
    this.filter()
  }

  // One { name, values, exact } per control name that holds a value.
  get criteria() {
    const byName = {}
    this.controlTargets.forEach((control) => {
      const value = this.currentValue(control)
      if (value === "") return
      byName[control.name] ||= { name: control.name, values: [], exact: !TEXT_TYPES.includes(control.type) }
      byName[control.name].values.push(value)
    })
    return Object.values(byName)
  }

  currentValue(control) {
    if (["radio", "checkbox"].includes(control.type) && !control.checked) return ""
    return control.value.trim().toLowerCase()
  }

  matches(row, { name, values, exact }) {
    const subject = (row.getAttribute(`data-filter-${name}`) || "").toLowerCase()
    return values.some((value) => (exact ? subject === value : subject.includes(value)))
  }
}
